<template>
  <BaseModal
    v-if="modalIsOpen"
    :actions="true"
    :modifiers="['flat-title']"
    :title="$t(`inspections.${isNew ? 'new' : 'edit'}_inspection`)"
    @dismiss-modal="$emit('update:modalIsOpen', false)"
  >
    <template #content>
      <div class="form form--lighter form--no-peek form--borders">
        <div
          v-if="operationData.name !== 'shafts'"
          class="form__group"
        >
          <FormRow
            :label="$t('activerecord.attributes.operation/inspection.type')"
            :modifiers="['lateral']"
          >
            <div class="form-row__input">
              <Select
                v-model="inspection.inspection_type"
                :items="dropDownOptions"
              />
            </div>
          </FormRow>
        </div>
        <div class="form__group">
          <FormRow
            :errors="errors && errors.inspection.date"
            :label="$t('activerecord.attributes.operation/inspection.date')"
            :modifiers="['lateral']"
          >
            <div class="form-row__input">
              <FormDatePicker
                v-model="inspection.date"
                @change="clearValidation"
              />
            </div>
          </FormRow>
        </div>
        <div
          v-if="operationData.name !== 'shafts'"
          class="form__group"
        >
          <FormRow
            :label="$t('inspections.inspector')"
            :modifiers="['lateral']"
          >
            <div class="form-row__input">
              <Select
                v-model="inspection.inspected_by_id"
                :disabled="!initialInspection?.trusted_user"
                :items="inspectors"
              />
            </div>
          </FormRow>
        </div>
        <div
          v-if="operationData.name !== 'shafts'"
          class="form__group preng"
        >
          <div class="form-row form-row--lateral preng__form-row">
            <div class="form-row__fields">
              <input
                id="preng"
                v-model="inspection.professional_engineer"
                class="checkbox__input"
                type="checkbox"
                :value="inspection.professional_engineer"
              >
              <label
                class="checkbox__label"
                for="preng"
              >
                {{ $t('inspections.performed_by_preng') }}
              </label>

              <div class="preng__form-info">
                <PrEngBadge class="icon" />
                <InfoPopup>
                  <template #trigger>
                    <IEncircledSvg />
                  </template>

                  {{ $t('inspections.preng_description') }}
                </InfoPopup>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #actions>
      <FormSubmitButton
        :buttonLabel="submitButtonText"
        :isSaving="saving"
        @click="saveInspection"
      />
    </template>
  </BaseModal>
</template>

<script setup>
import { computed, ref, watch, inject, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import I18n from 'i18n-js';

// Components
import BaseModal from '@/components/generic/base_modal.vue';
import InfoPopup from '@/components/generic/info_popup.vue';
import Select from '@/components/form/select.vue';
import FormDatePicker from '@/components/form/date_picker.vue';
import FormRow from '@/components/form/row.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';

// Assets
import PrEngBadge from '@/images/rosette_24.svg';
import IEncircledSvg from '@/images/i_encircled.svg';

// Functions
import useAjax from '@/functions/use_ajax';
import { isoDate } from '@/functions/date_format_helpers';
import useOrganisationProfile from '@/functions/use_organisation_profile'; 


const createNewInspection = () => ({
  inspection_type: '',
  date: new Date(),
  inspected_by_id: '',
  professional_engineer: null,
});

const props = defineProps({
  initialInspection: { type: Object, required: true },
  modalIsOpen: Boolean,
});
const emit = defineEmits(['save', 'update:modalIsOpen']);

const ajax = useAjax();
const route = useRoute();
const inspection = ref(createNewInspection());
const saving = ref(false);
const errors = ref(null);
const inspectors = ref([]);
const loading = ref(true);
const currentUser = inject('currentUser');

const { isAga, inspectionTypeName } = useOrganisationProfile();
const isShaftV2 = computed(() => route.path.includes('shafts_v2'));

const dropDownOptions = computed(() => {
  let types;

  if (isShaftV2.value) {
    types = isAga.value
      ? ['general', 'structural', 'special', 'simm_audit']
      : ['general', 'structural', 'special'];
  } else {
    types = isAga.value
      ? ['annual', 'simm_audit', 'ad_hoc']
      : ['annual', 'ad_hoc'];
  }

  return types.map((value) => ({
    value,
    label: inspectionTypeName(value),
    details: { description: I18n.t(`inspection_types.${value}.description`) },
  }));
});

const isNew = computed(() => !props.initialInspection.id);

const submitButtonText = computed(() => {
  return I18n.t(`inspections.${isNew.value ? 'create' : 'save'}_inspection`);
});

const operationData = computed(() => {
  if (route.params.shaftId && isShaftV2.value) {
    return { name: 'shaftV2', url: `shafts_v2/shafts/${route.params.shaftId}` };
  } else if (route.params.shaftId) {
    return { name: 'shafts', url: `shafts/${route.params.shaftId}` };
  } else if (route.params.shipId) {
    return { name: 'ships', url: `ships/${route.params.shipId}` };
  } else if (route.params.equipmentId) {
    return { name: 'equipment', url: `equipment/${route.params.equipmentId}` };
  } else {
    const url = `operations/${route.params.operationId}`;
    return { name: 'plants', url: `${url}/plants`, inspectorsUrl: url };
  }
});

onMounted(async () => {
  await loadInspectors();
});

watch(
  () => props.modalIsOpen,
  () => {
    if (props.modalIsOpen) {
      inspection.value = {
        inspection_type: props.initialInspection.inspection_type,
        date: new Date(props.initialInspection.date),
        inspected_by_id: props.initialInspection.inspected_by_id || currentUser?.id,
        professional_engineer: props.initialInspection.professional_engineer,
      };
    }
  }
);

async function saveInspection() {
  saving.value = true;
  errors.value = null;
  let path, method;

  if (isNew.value) {
    path = `/organisations/${route.params.organisationId}/${operationData.value.url}/inspections`;
    method = 'POST';
  } else {
    path = `/organisations/${route.params.organisationId}/${operationData.value.url}/inspections/${props.initialInspection.id}`;
    method = 'PATCH';
  }

  let { ok, data } = await ajax(path, {
    method,
    body: {
      inspection: {
        date: isoDate(inspection.value.date),
        inspection_type: inspection.value.inspection_type,
        inspected_by_id: inspection.value.inspected_by_id,
        professional_engineer: inspection.value.professional_engineer,
      },
    },
  });

  if (ok) {
    emit('update:modalIsOpen', false);
    emit('save', data);
  } else {
    errors.value = data;
  }
  saving.value = false;
}

function clearValidation() {
  errors.value = null;
}

async function loadInspectors() {
  if (operationData.value.name === 'shafts') return;

  loading.value = true;
  let endpoint = operationData.value?.inspectorsUrl || operationData.value.url;

  let { ok, data } = await ajax(`/organisations/${route.params.organisationId}/${endpoint}/inspectors?assignable=true`);

  if (ok) {
    inspectors.value = data.map((item) => ({
        value: item.id,
        label: item.full_name,
      })
    );
  }
  loading.value = false;
}
</script>
